import { Box, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, Slide, Stack, Switch, TextField, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { AccessLevelList, AddStaff, EditStaff, getStaffData } from "../../Api/Staff";
import { getAllStoreName } from "../../Api/Store";
import { AccessLevel } from "../../Objects/AccessLevel";
import { StaffData } from "../../Objects/StaffData";
import { StoreData } from "../../Objects/StoreInterface";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface StaffEditDialogProps {
    stfId?: number
    onClose: (isEdit: boolean) => void
}

const StaffEditDialog: React.FC<StaffEditDialogProps> = ({
    stfId,
    onClose,
}) => {
    let { enqueueSnackbar } = useSnackbar()

    const [open, setOpen] = React.useState<boolean>(false)
    const [staffData, setStaffData] = React.useState<StaffData>({})
    const [accessLevelList, setAccessLevelList] = React.useState<AccessLevel[]>()
    const [storeDataList, setStoreDataList] = React.useState<StoreData[]>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doSave = React.useCallback(() => {
        if (stfId === 0) {
            AddStaff({
                staffData: staffData
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("登録しました", { variant: "success" })
                setOpen(false)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        } else {
            EditStaff({
                staffData: staffData
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("登録しました", { variant: "success" })
                setOpen(false)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, staffData, stfId, enqueueSnackbar])

    /**
     * データ初期化
     */
    React.useEffect(() => {
        const controller = new AbortController()
        setOpen(false)
        if (stfId === 0) {
            // 新規
            setStaffData({})
            setOpen(true)
        } else if (stfId) {
            getStaffData({
                stf_id: stfId
            }, {signal: controller.signal}).then(result => {
                setStaffData(result.staffData)
                setOpen(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
        return () => {
            controller.abort()
        }
    }, [stfId, enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        AccessLevelList(undefined, {signal: controller.signal}).then(result => {
            setAccessLevelList(result)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        getAllStoreName({signal: controller.signal}).then(result => {
            setStoreDataList(result)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        return () => {
            controller.abort()
        }
    }, [enqueueSnackbar])

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={() => {
                setOpen(false)
            }}
            TransitionProps={{
                onExited: () => {
                    onClose(false)
                }
            }}
            fullWidth
        >
            <DialogTitle>
                <Grid container
                    justifyContent={"space-between"}
                    alignItems={"center"}
                >
                    <Grid item>{(stfId && stfId > 0) ? "スタッフデータ編集" : "新規スタッフ追加"}</Grid>
                    <Grid item>
                        <Box>
                            <Stack
                                direction={"row"}
                                spacing={2}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setOpen(false)
                                    }}
                                >キャンセル</Button>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        doSave()
                                    }}
                                >登録</Button>
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>

            </DialogTitle>
            <DialogContent>
                <Stack
                    spacing={1}
                >
                    <TextField
                        variant="filled"
                        label="表示名"
                        value={staffData.stf_name ? staffData.stf_name : ""}
                        onChange={e => {
                            setStaffData(prev => {
                                return {
                                    ...prev,
                                    stf_name: e.target.value
                                }
                            })
                        }}
                        helperText="【必須】表示に使用するスタッフ表示名を入力してください。2〜32文字の文字列が設定可能です。"
                        size="small"
                        required
                    />
                    <TextField
                        variant="filled"
                        label="スタッフログイン名"
                        value={staffData.login_str ? staffData.login_str : ""}
                        onChange={e => {
                            setStaffData(prev => {
                                return {
                                    ...prev,
                                    login_str: e.target.value
                                }
                            })
                        }}
                        helperText="【必須】ログイン時に使用するログイン名を入力してください。半角英数字を使用した4〜24文字が設定可能です。"
                        size="small"
                        required
                    />
                    <TextField
                        variant="filled"
                        label="スタッフ認証コード"
                        value={staffData.stf_code ? staffData.stf_code : ""}
                        onChange={e => {
                            setStaffData(prev => {
                                return {
                                    ...prev,
                                    stf_code: e.target.value
                                }
                            })
                        }}
                        helperText="【必須】スタッフ認証コード用のバーコードを入力してください"
                        size="small"
                        required
                    />
                    {!stfId &&
                        <>
                            <TextField
                                variant="filled"
                                label="ログインパスワード"
                                value={staffData.login_pass ? staffData.login_pass : ""}
                                onChange={e => {
                                    setStaffData(prev => {
                                        return {
                                            ...prev,
                                            login_pass: e.target.value
                                        }
                                    })
                                }}
                                type="password"
                                helperText="ログイン時に使用するパスワードを入力してください。8〜24文字が設定可能で、半角英数字とハイフン、アンダーバー、が利用可能です。"
                                size="small"
                            />
                            <TextField
                                variant="filled"
                                label="ログインパスワード（確認）"
                                value={staffData.login_pass_confirm ? staffData.login_pass_confirm : ""}
                                onChange={e => {
                                    setStaffData(prev => {
                                        return {
                                            ...prev,
                                            login_pass_confirm: e.target.value
                                        }
                                    })
                                }}
                                type="password"
                                helperText="ログインパスワードで設定したパスワードを再度入力してください。"
                                size="small"
                            />
                        </>
                    }
                    <TextField
                        variant="filled"
                        label="メールアドレス"
                        value={staffData.stf_mail ? staffData.stf_mail : ""}
                        onChange={e => {
                            setStaffData(prev => {
                                return {
                                    ...prev,
                                    stf_mail: e.target.value
                                }
                            })
                        }}
                        type="email"
                        helperText="受信可能なメールアドレスを入力してください。"
                        size="small"
                    />
                    <TextField
                        variant="filled"
                        label="電話番号"
                        value={staffData.stf_tel ? staffData.stf_tel : ""}
                        onChange={e => {
                            setStaffData(prev => {
                                return {
                                    ...prev,
                                    stf_tel: e.target.value
                                }
                            })
                        }}
                        type="tel"
                        helperText="連絡可能な電話番号を入力してください。"
                        size="small"
                    />
                    {accessLevelList &&
                        <Box>
                            <Stack>
                                <Typography variant="subtitle2">アクセス権限</Typography>
                                <Grid container>
                                    {accessLevelList.map(accessLevel => (
                                        <Grid item key={accessLevel.acle_id}>
                                            <FormControlLabel
                                                label={accessLevel.acle_name}
                                                control={
                                                    <Switch
                                                        color="secondary"
                                                        checked={staffData.acle_id ? staffData.acle_id === accessLevel.acle_id : false}
                                                        onChange={e => {
                                                            setStaffData(prev => {
                                                                return {
                                                                    ...prev,
                                                                    acle_id: e.target.checked ? accessLevel.acle_id : undefined
                                                                }
                                                            })
                                                        }}
                                                    />
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>

                            </Stack>
                        </Box>
                    }
                    {storeDataList &&
                        <Box>
                            <Stack>
                                <Typography variant="subtitle2">管理店舗</Typography>
                                <Grid container>
                                    {storeDataList.map(storeData => (
                                        <Grid item key={storeData.str_id}>
                                            <FormControlLabel
                                                label={storeData.str_name ?? ""}
                                                control={
                                                    <Switch
                                                        color="secondary"
                                                        checked={(staffData.admin_str_id_list && storeData.str_id) ? staffData.admin_str_id_list.includes(storeData.str_id) : false}
                                                        onChange={e => {
                                                            setStaffData(prev => {
                                                                let list = prev.admin_str_id_list ? prev.admin_str_id_list.filter(id => id !== storeData.str_id) : []
                                                                if (e.target.checked && storeData.str_id) {
                                                                    list.push(storeData.str_id)
                                                                }
                                                                return {
                                                                    ...prev,
                                                                    admin_str_id_list: list
                                                                }
                                                            })
                                                        }}
                                                    />
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>

                            </Stack>
                        </Box>
                    }
                </Stack>
            </DialogContent>
        </Dialog>
    );
}

export default StaffEditDialog